import { apiClient } from "./apiClient"

/****************************************************************************
 * 
 * Tasks
 * 
 ***************************************************************************/
export const taskApi = {
    /**
     * Fetch Task List for a given Business Entity ID
     * @param {string} businessEntityId - The Business Entity ID
     * @param {object} filter - The filter object containing the predicates array
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The UTG list as a Promise
     */
    fetchTaskList: async (businessEntityId, filters, config, accessToken, signal=null) => {
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/tasks/query`
        const componentsFilter = filters ? JSON.stringify(filters) : '{"Predicates": []}'
        const body = JSON.stringify(componentsFilter).replace("\n", "")
        return await apiClient(
            url,
            accessToken,
            {
                method: 'POST',
                // make sure we encode the query correctly
                body: body,
                headers: {
                    'clientId': 4
                }
            },
            async (response) => {
                return await response.json().then(
                    (result) => {
                        return result.taskView
                    }
                )
            },
            signal
        )
    },
    /**
     * Create New Task
     * @param {string} businessEntityId - The Business Entity ID
     * @param {string} machinePermanentId - The Machine Permanent ID
     * @param {object} taskInstructions - The Profile ID
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The update result as a Promise
     */
    createTask: async (businessEntityId, machinePermanentId, taskInstructions, config, accessToken) => {
        if (typeof businessEntityId == 'undefined') {
            throw new Error("Undefined business entity ID")
        } else if (typeof machinePermanentId == 'undefined') {
            throw new Error("Undefined machine permanent ID")
        }
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/machines/${machinePermanentId}/tasks`
        // IMPORTANT: Note that this is PUT rather than POST.
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST",
                headers: {
                    'Clientid': '4'
                },
                body: JSON.stringify(taskInstructions)
            }
        )
    },
    createTasks: async (businessEntityId, taskInstructions, config, accessToken) => {
        if (typeof businessEntityId == 'undefined') {
            throw new Error("Undefined business entity ID")
        }
        if (!taskInstructions) {
            throw new Error("Undefined trask instructions")
        }
        if (!config) {
            throw new Error("Config is undefined")
        }
        if (!accessToken) {
            throw new Error("Access token is undefined")
        }
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/tasksbulk`
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST",
                headers: {
                    'Clientid': '4'
                },
                body: JSON.stringify(taskInstructions)
            }
        )
    },
    /**
     * Delete Task
     * @param {string} businessEntityId - The Business Entity ID
     * @param {string} machinePermanentId - The Machine Permanent ID
     * @param {object} taskId - The Task ID
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The update result as a Promise
     */
    deleteTask: async (businessEntityId, machinePermanentId, taskId, config, accessToken) => {
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/machines/${machinePermanentId}/tasks/${taskId}`
        // IMPORTANT: Note the server requires POST with a header override to indicate DELETE
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST",
                headers: {
                    'X-HTTP-Method-Override': 'DELETE'
                }
            },
            (response) => {
                return response
            }
        )
    },
    /**
     * Export Tasks
     * @param {string} businessEntityId - The Business Entity ID
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The update result as a Promise
     */
    exportTasks: async (businessEntityId, config, accessToken) => {
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/tasks/query/csv`
        const tasksFilter = '{"Predicates": []}'
        // IMPORTANT: Note that this is PUT rather than POST.
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST",
                headers: {
                    'Clientid': '4'
                },
                body: JSON.stringify(tasksFilter).replace("\n", "") // make sure we encode the query correctly
            },
            async (response) => {
                return await response.arrayBuffer()
            }
        )
    }
}